import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faKey, faUserLock, faLock } from '@fortawesome/pro-regular-svg-icons';

import type { AccountResourceType } from '../store';

/**
 * Shown in place of a data-retention account's name when it has none. Every account
 * created before account management existed has `name: ''` — the settings flow
 * persisted an empty string literally, and the `accountDefinitionName` backfill left
 * names untouched — so these render as a blank label on the storage card and in the
 * account picker.
 *
 * Deliberately static rather than derived from the bucket or endpoint: a workspace has
 * exactly one data-retention account, so there is nothing to disambiguate. It is also
 * strictly a display fallback and must never be written back — config-backend's
 * adopt-or-create resolver identifies these same pre-AMF accounts *by* their empty name
 * (`LEGACY_SETTINGS_MANAGED_ACCOUNT_NAME = ''`), and naming them would retire that path.
 * The account edit form is therefore left prefilling the stored `name`, not this.
 */
const DATA_RETENTION_FALLBACK_ACCOUNT_NAME = 'Data retention account';

/**
 * The label to render for an account. Scoped to data retention: the source and
 * destination create surfaces both reject an empty name, so only data-retention
 * accounts can reach a render with one, and every other resource type renders exactly
 * what it stores.
 *
 * `||` rather than the house-preferred `??` is the point — `''` is the value being
 * replaced, and a nullish check would let it through unchanged.
 */
export const getAccountDisplayName = (name: string, resourceType: AccountResourceType): string =>
  resourceType === 'dataRetention' ? name || DATA_RETENTION_FALLBACK_ACCOUNT_NAME : name;

const AUTH_TYPE_ICON_MAP: { [key: string]: IconDefinition } = {
  key: faKey,
  'user-lock': faUserLock,
  lock: faLock,
};

export const getAuthTypeIcon = (iconKey: string): IconDefinition =>
  AUTH_TYPE_ICON_MAP[iconKey] || faKey;

// Helper function to extract metadata values as strings, ignoring object-type values
export const getMetadataSearchString = (metadata?: Record<string, unknown>): string => {
  if (!metadata) {
    return '';
  }

  // Extract all values from metadata object
  return Object.values(metadata)
    .filter(
      (value) =>
        // Filter out null, undefined, and object-type values
        value !== null && value !== undefined && typeof value !== 'object',
    )
    .map((value) => String(value)) // Convert remaining values to strings
    .join(' '); // Join them into a single searchable string
};
